import React from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import _ from 'lodash';
import Highcharts from 'highcharts';
import Exporting from 'highcharts/modules/exporting';
import OfflineExporting from 'highcharts/modules/offline-exporting.src';
import Draggable from 'react-draggable';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import withChartExport from '../../../components/ChartExporting';

import highchartFactory from '../../../util/highcharts';
const highchartUtils = highchartFactory(Highcharts);

import { saveConfig, updateAvgChart, updateWidgetMode } from '../../../components/Filter/FilterActions';
import dateUtils from '../../../util/dates';

import expandIcon from '../../../assets/img/zoom-icon-off.svg';
import collapseIcon from '../../../assets/img/zoom-icon-on.svg';
import barIcon from '../../../assets/img/bar-icon.svg';
import lineIcon from '../../../assets/img/line-chart-icon-on.svg';
import arrowDownIcon from '../../../assets/img/arrow_down.svg';
import zoomOnIcon from '../../../assets/img/magnifier-on-01.svg';

import './ArrivalDepChart.scss';

const lineConfig = {
  chart: {
    type: 'spline',
    backgroundColor: '#fff',
    events: {},
  },
  exporting: {
    enabled: false,
  },
  credits: {
    enabled: false
  },
  title: {
    text: ''
  },
  legend: {
    enabled: false
  },
  colors: ['#00a2e3', '#007baa'],
  tooltip: {
    shared: true,
    useHTML: true,
    formatter: function() {
      let tooltipHtml = '',
        s = [];
      this.points.forEach(point => {
        tooltipHtml += `<div class="tooltip-label-item"><span class="label-box" style="background: ${
          point.color
        }"></span>${point.series.name} rate: ${point.point.y.toFixed(2)} item${point.point.y != 1 ? 's' : ''}/day</div>`;
      });
      s.push(tooltipHtml);
      return s;
    }
  },
  xAxis: {
    type: 'datetime',
    gridLineWidth: 0,
    showFirstLabel: true,
    showLastTickLabel: true,
    title: {
      text: null
    }
  },
  yAxis: {
    gridLineWidth: 0,
    labels: {
      enabled: false
    },
    title: {
      text: null
    },
    showFirstLabel: false
  },
  plotOptions: {
    spline: {
      marker: {
        symbol: 'circle',
        enabledThreshold: 2,
        lineWidth: 1,
        radius: 5
      },
      states: {
        hover: {
          lineWidth: 2,
          halo: {
            opacity: 0
          },
          marker: {
            enabled: true
          }
        }
      }
    }
  },
  series: [
    {
      data: []
    } 
  ]
};

const barConfig = {
  chart: {
    type: 'column',
    backgroundColor: '#fff',
    events: {},
  },
  exporting: {
    enabled: false,
  },
  credits: {
    enabled: false
  },
  title: {
    text: ''
  },
  legend: {
    enabled: false
  },
  colors: ['#00a2e3', '#007baa'],
  xAxis: {
    type: 'datetime',
    gridLineWidth: 0,
    showFirstLabel: true,
    showLastTickLabel: true,
    labels: {
      align: 'center',
      x: 0,
      rotation: 0
    },
    title: {
      text: null
    }
  },
  yAxis: {
    gridLineWidth: 0,
    labels: {
      enabled: true
    },
    title: {
      text: null
    },
    showFirstLabel: false
  },
  tooltip: {
    enabled: false
  },
  plotOptions: {
    series: {
      events: {
        legendItemClick: function () {}
      }
    },
    column: {
      stacking: 'normal',
    }
  }
};

const DEFAULT_POS = { x: 95, y: 85 };

function renderNetStats(chart, stats) {
  const netStats = [];

  if (chart.options.chart.spacingBottom > 10) {
    const chartHeight = chart.plotHeight + chart.plotTop;
    const totalHeight = chart.chartHeight;
    const spacingBottom = chart.options.chart.spacingBottom;
    const legendHeight = totalHeight - chartHeight - spacingBottom;

    const rectHeight = spacingBottom - 40;

    const bgRect = chart.renderer.rect(chart.plotLeft, chartHeight + legendHeight + 20, chart.plotWidth, rectHeight)
      .attr({ 'fill': '#f0f2f5','stroke-width': 0, 'r': 8 })
      .add();

    netStats.push(bgRect);

    const titleY = chartHeight + legendHeight + 40;
    const title = chart.renderer.text(stats.title, chart.plotLeft + 10, titleY)
      .css({ fontSize: '14px', fontWeight: 'bold', color: '#333' })
      .add();

    netStats.push(title);

    let currentY = titleY + 20;

    stats.listed.forEach((itemName, index) => {
      const item = _.find(stats.data, { name: itemName });
      if (index > 0) {
        const separator = chart.renderer.path([
          'M', chart.plotLeft + 15, currentY,
          'L', chart.plotLeft + chart.plotWidth - 15, currentY
        ])
          .attr({ 'stroke-width': 1, 'stroke': '#ddd' })
          .add();

          netStats.push(separator);
          currentY += 10;
      }

      const rowY = currentY + 12;
      const diff = item.totalArrival - item.totalThroughput;

      const colorSquare = chart.renderer.rect(chart.plotLeft + 15, rowY - 10, 12, 12)
        .attr({ 'fill': item.color, 'stroke-width': 0, 'r': 2 })
        .add();

      netStats.push(colorSquare);

      const nameText = chart.renderer.text(item.name, chart.plotLeft + 32, rowY)
        .css({ fontSize: '12px', color: '#333' })
        .add();

      netStats.push(nameText);

      let inOutText;
      if (!stats.noBreakdown) {
        inOutText = `${item.totalArrival} in / ${item.totalThroughput} out`;
      } else {
        inOutText = `${item.totalArrival} arrived / ${item.totalThroughput} delivered`;
      }

      const inOut = chart.renderer.text(inOutText, 0, rowY)
        .css({ fontSize: '12px', color: '#666' })
        .add();

      const labelWidth = 110;
      const inOutBBox = inOut.getBBox();
      inOut.attr({ x: chart.plotLeft + chart.plotWidth - 15 - labelWidth - 10 - inOutBBox.width });

      netStats.push(inOut);

      const labelText = diff <= 0 ? `${diff} clearing` : `${diff} accumulating`;
      const labelColor = diff <= 0 ? '#28a745' : '#dc3545';
      const labelBgColor = diff <= 0 ? 'rgba(40, 167, 69, 0.1)' : 'rgba(220, 53, 69, 0.1)';

      const label = chart.renderer.text(labelText, 0, rowY)
        .css({ fontSize: '11px', color: labelColor, fontWeight: '500' })
        .add();

      const labelBBox = label.getBBox();
      const labelPadding = 5;

      const labelBg = chart.renderer.rect(
        chart.plotLeft + chart.plotWidth - 15 - labelWidth,
        rowY - 10 - labelPadding,
        labelWidth,
        14 + (labelPadding * 2)
      )
      .attr({ 'fill': labelBgColor, 'stroke-width': 0, 'r': 3 })
      .add();
      netStats.push(labelBg);

      label.attr({
        x: chart.plotLeft + chart.plotWidth - 15 - labelWidth + (labelWidth - labelBBox.width) / 2
      });
      netStats.push(label);

      currentY += 25;
    });
  }
  return netStats;
}

export class ArrivalDepChart extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isFullScreen: false,
      isTrendChart: true,
      isLabelsVisible: true,
      isStatsVisible: true,
      width: undefined,
      height: undefined,
      pos: DEFAULT_POS,
      netStatsConfig: { title: '', data: [], listed: [] }
    };

    this.netStatsConfig = {title: '', data: [], listed: []};
  }

  static defaultProps = {
    data: {}
  };

  componentDidMount() {
    highchartUtils.setDefaults();
    Exporting(Highcharts);
    OfflineExporting(Highcharts);

    this.setState({ isTrendChart: this.props.widgetMode === 'line' }, () => this.initChart());
  }

  componentDidUpdate(prevProps) {
    if (!_.isEqual(prevProps.data, this.props.data)) {
      this.calculateChartData();
      this.updateFontSize();
    }
    if (
      prevProps.dateFormat != this.props.dateFormat ||
      prevProps.timeUnit != this.props.timeUnit
    ) {
      this.updateDateFormat();
    }
    if (prevProps.widgetMode != this.props.widgetMode) {
      this.updateChartType();
    }
  }

  componentWillUnmount() {
    if (this.chart) {
      this.chart.destroy();
    }
  }

  initChart() {
    if (this.chart) {
      this.chart.destroy();
    }
    const config = this.props.widgetMode === 'line' ? lineConfig : barConfig;

    if (this.props.widgetMode === 'bar') {
      let that = this;
      config.tooltip.formatter = function () {
        return that.tooltipFormatter(this, that);
      };
      config.plotOptions.series.events.legendItemClick = function () {
        setTimeout(() => that.updateFontSize(), 0);
      }
    }

    config.exporting = { ...config.exporting, ...this.props.chartExporting };
    this.chart = new Highcharts['Chart'](`arrival_dep_chart_container`, config);

    this.updateDateFormat();
    this.calculateChartData();
    this.updateFontSize();
  }


  tooltipFormatter(chartScope, componentScope) {
    const point = chartScope;
    const userOptions = point.series.userOptions;
    const labelFormat = componentScope.getDateFormatMoment();
    const netValue = point.point.options.arrival - point.point.options.throughput;
    const noBreakdown = userOptions.name === userOptions.stack;

    const subheader = `<div class="heading"><div>${userOptions.name}</div><div>${moment(point.x).format(labelFormat)}${noBreakdown ? '' : ` · ${userOptions.stack}`}</div></div>`
    const header = `<div class="demand-vs-capability-header"><div class="circle-dot" style="background: ${point.color}"></div>${subheader}</div>`;
    const arrived = `<div class="demand-vs-capability-body"><div>Arrived</div><div>${point.point.options.arrival} items</div></div>`;
    const delivered = `<div class="demand-vs-capability-body"><div>Delivered</div><div>${point.point.options.throughput} items</div></div>`;
    const percentage = `<div class="demand-vs-capability-body"><div>Share of ${userOptions.stack.toLowerCase()}</div><div>${point.percentage.toFixed(2)} %</div></div>`;
    const total = `<div class="demand-vs-capability-body"><div>Total ${userOptions.stack.toLowerCase()}</div><div>${point.total} items</div></div>`;
    const net = `<div class="demand-vs-capability-net"><div>Net</div><div style="color: ${ netValue > 0 ? 'red' : 'green'};">${netValue > 0 ? `+${netValue} accumulating` : `${netValue} clearing`}</div></div>`
    const tooltip = `<div class="demand-vs-capability-tooltip" style="margin: 8px 8px 0;">${header}<hr/>${arrived}${delivered}${percentage}${total}<hr/>${net}</div>`;

    return tooltip;
  }

  updateChartType() {
    this.setState({ isTrendChart: this.props.widgetMode === 'line' }, () => this.initChart());
  }

  getDateFormatMoment() {
    const momentFormat = this.props.dateFormat || 'DD MMM YYYY';
    const timeUnit = this.props.timeUnit || 'month';
    let labelFormat;
    
    switch (timeUnit) {
      case 'day':
      case 'week':
      case '2weeks':
        labelFormat = momentFormat.replace('YYYY', '').trim();
        break;
      case 'month':
      default:
        labelFormat = momentFormat.replace('DD', '').trim();
        break;
    }

    return labelFormat;
  }

  getDateFormat() {
    if (!this.props.dateFormat) return;
    const format = dateUtils.createHighchartFormats(this.props.dateFormat);
    const timeUnit = this.props.timeUnit || 'month';

    let labelFormat;
    switch (timeUnit) {
      case 'day':
        labelFormat = format.day;
        break;
      case 'week':
      case '2weeks':
        labelFormat = format.week;
        break;
      case 'month':
      default:
        labelFormat = format.month;
        break;
    }

    return labelFormat;
  }

  updateDateFormat() {
    if (!this.chart || !this.props.dateFormat || !this.props.data || !this.props.data.length) {
      return;
    } 
    
    const labelFormat = this.getDateFormat();

    this.chart.update({
      xAxis: {
        dateTimeLabelFormats: {
          millisecond: labelFormat,
          second: labelFormat,
          minute: labelFormat,
          hour: labelFormat,
          day: labelFormat,
          week: labelFormat,
          month: labelFormat
        }
      }
    });
  }

  calculateChartData() {
    if (!this.chart || !this.props.dateFormat || !this.props.data || !this.props.data.length) {
      return;
    } 
    while (this.chart.series.length > 0) {
      this.chart.series[0].remove(false);
    }

    if (!this.props.data.length) {
      return false;
    }

    const showInLegend = {};
    const series = [];

    const breakdownOptions = !this.state.isTrendChart && 
      _(this.props.data).flatMap('data').flatMap('breakdown').compact().uniqBy('x').value();

    this.props.data.forEach((group, index) => {
      if (this.state.isTrendChart) {
        series.push({ ...group, showInLegend: true, color: ['#00a2e3', '#007baa'][index] });
      } else {
        group.data.forEach(d => {
          if (breakdownOptions && breakdownOptions.length) {
            _.forEach(breakdownOptions, opt => {
              const b = _.find(d.breakdown, { x: opt.x });
              const s = _.find(series, { name: opt.x, stack: group.name });
              if (s) {
                s.data.push({ x: d.x, y: b?.total || 0, arrival: b?.arrival || 0, throughput: b?.throughput || 0 });
              } else {
                series.push({
                  name: opt.x,
                  color: opt.color || 'rgba(124,181,236, 0.7)',
                  stack: group.name,
                  id: !showInLegend[opt.x] ? `category-${opt.x}` : undefined,
                  linkedTo: !showInLegend[opt.x] ? undefined : `category-${opt.x}`,
                  showInLegend: !showInLegend[opt.x],
                  data: [{ x: d.x, y: b?.total || 0, arrival: b?.arrival || 0, throughput: b?.throughput || 0 }]
                });
                showInLegend[opt.x] = true;
              }
            });           
          } else {
            const s = _.find(series, { name: group.name });
            if (s) {
              s.data.push({ x: d.x, y: d.total, arrival: d.totalArrival, throughput: d.totalThroughput });
            } else {
              series.push({
                ...group,
                stack: group.name,
                showInLegend: true,
                color: ['#00a2e3', '#007baa'][index],
                data: [{ x: d.x, y: d.total, arrival: d.totalArrival, throughput: d.totalThroughput }]
              });
            }
          }
        });
      }
    });

    series.forEach(s => this.chart.addSeries(s, false));

    this.setNetStatsConfig(series, this.props.data[0].periods);

    this.chart.update({
      xAxis: {
        plotLines: [
          {
            color: '#F5F5F5',
            width: 1,
            value: this.props.data[0].data[0].x,
            label: {
              text: ''
            }
          }
        ]
      }
    });
    this.chart.redraw();
  }

  getBreakdownByName() {
    const { breakdownBy, customFields, app } = this.props;
    if (breakdownBy === 'labels') {
      switch (app) {
        case 'azure': return 'Work item types';
        case 'asana': return 'Tags';
        case 'jira': return 'Priorities';
        case 'trello':
        default: return 'Labels';
      }
    } else if (breakdownBy && customFields) {
      const customField = _.find(customFields, { extId: breakdownBy });
      if (customField) {
        return customField.name;
      }
    } 
  }

  setNetStatsConfig(series, periods) {
    if (!this.state.isTrendChart) {
      const { timeUnit } = this.props;
      const breakdownByName = this.getBreakdownByName();

      let rangeLabel = 'All time';
      let periodsNumber = '';
      switch (timeUnit) {
        case 'day': rangeLabel = `${periods}-Days`; break;
        case 'week': rangeLabel = `${periods}-Weeks`; break;
        case '2weeks': rangeLabel = `${periods * 2}-Weeks`; break;
        case 'month': rangeLabel = `${periods}-Month${ periods > 1 ? 's' : '' }`; break;
        default: rangeLabel = 'All time'
      }

      this.netStatsConfig.title = `${rangeLabel} Net Accumulation${breakdownByName ? ` by ${breakdownByName}`: ''}`;
      this.netStatsConfig.data = _.uniqBy(series, 'name').map(s => ({
        name: s.name,
        color: s.color,
        totalArrival: s.totalArrival || _.sumBy(s.data, 'arrival'),
        totalThroughput: s.totalThroughput || _.sumBy(s.data, 'throughput'),
      }));
      this.netStatsConfig.listed = _.map(this.netStatsConfig.data, 'name');
      this.netStatsConfig.noBreakdown = !breakdownByName;
      this.setState({ netStatsConfig: { ...this.netStatsConfig } });
    }
  }

  updateNetStatsVisibility() {
    if (this.chart && this.netStatsConfig.data) {
      this.netStatsConfig.listed = this.chart.series
        .filter(s => s.visible && s.options.showInLegend)
        .map(s => s.name);
      this.setState({ netStatsConfig: { ...this.netStatsConfig } });
    }
  }

  updateFontSize = () => {
    if (!this.chart) {
      return;
    }

    const { isLabelsVisible, isFullScreen, isTrendChart } = this.state;
    const showLabels = isFullScreen || !isTrendChart;
    const showLegend = isTrendChart ? isFullScreen : isLabelsVisible;

    const that = this;
    const tooltip = isTrendChart ? lineConfig.tooltip : isFullScreen ? {
      shared: false,
      useHTML: true,
      enabled: true,
      formatter: function () { return that.tooltipFormatter(this, that); }
    } : { enabled: false };

    this.updateNetStatsVisibility();

    const netStatsConfig = this.netStatsConfig;

    this.chart.update({
      exporting: {
        enabled: isFullScreen,
        chartOptions: {
          title: {
            text: 'Arrival & Throughput Rates',
            style: {
              fontSize: '16px',
              fontWeight: 'bold'
            }
          },
          chart: {
            events: {
              redraw: function () {
                renderNetStats(this, netStatsConfig);
              },
              load: function () {
                renderNetStats(this, netStatsConfig);
              }
            }
          }
        }
      },
      legend: {
        enabled: showLegend,
        useHTML: true,
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        maxHeight: 47,
        symbolWidth: 10,
        symbolHeight: 10,
        symbolRadius: 5,
        itemMarginBottom: 5,
        navigation: {
          enabled: true
        },
        labelFormatter: isTrendChart ?
          function () { return `<span class="color:#333333;font-size:12px;font-weight:normal">${this.name} Rate</span>` } :
          function () { return `<span class="color:#333333;font-size:12px;font-weight:normal">${this.name}</span>` }
      },
      tooltip: tooltip,
      yAxis: {
        gridLineWidth: +isFullScreen,
        labels: {
          enabled: showLabels
        },
        title: {
          text: null
        },
        showFirstLabel: false
      }
    });

    requestAnimationFrame(() => {
      if (this.chart) {
        this.chart.reflow();
      }
    });
  };


  handleClose = () => {
    this.props.close();
    this.props.saveConfig();
  };

  handleDrag = (e, data) => {
    this.setState({ pos: { x: data.x, y: data.y } });
  };

  handleResize = () => {
    if (this.state.isFullScreen) {
      this.setState(
        {
          width: undefined,
          height: undefined,
          left: undefined,
          isFullScreen: false,
          pos: DEFAULT_POS
        },
        () => this.updateFontSize()
      );
    } else {
      this.setState(
        {
          width: '100%',
          height: '100%',
          left: -10,
          isFullScreen: true,
          pos: { x: 0, y: 0 }
        },
        () => this.updateFontSize()
      );
    }
  };

  handleSwitch = () => {
    this.setState({ isTrendChart: !this.state.isTrendChart }, () => {
      this.props.updateWidgetMode(this.state.isTrendChart ? 'line' : 'bar');
      this.props.saveConfig();
    });
  };

  handleToggleLabels = () => {
    this.setState({ isLabelsVisible: !this.state.isLabelsVisible }, () => this.updateFontSize());
  }

  handleToggleStats = () => {
    this.setState({ isStatsVisible: !this.state.isStatsVisible }, () => this.updateFontSize());
  }

  renderNetStatsPanel = () => {
    const {
      isTrendChart, isFullScreen, isStatsVisible, netStatsConfig,
    } = this.state;
    if (isTrendChart || !isFullScreen || !isStatsVisible || netStatsConfig.listed.length === 0) {
      return null;
    }
    return (
      <div className="net-stats-panel">
        <div className="net-stats-title">{netStatsConfig.title}</div>
        <div className="net-stats-panel-scroll">
          {netStatsConfig.listed.map((itemName) => {
            const item = _.find(netStatsConfig.data, { name: itemName });
            if (!item) return null;
            const diff = item.totalArrival - item.totalThroughput;
            const inOutText = netStatsConfig.noBreakdown
              ? `${item.totalArrival} arrived / ${item.totalThroughput} delivered`
              : `${item.totalArrival} in / ${item.totalThroughput} out`;
            const labelText = diff <= 0 ? `${diff} clearing` : `${diff} accumulating`;
            return (
              <div key={itemName} className="net-stats-row">
                <div className="net-stats-color" style={{ background: item.color }} />
                <span className="net-stats-name">{item.name}</span>
                <span className="net-stats-inout">{inOutText}</span>
                <span
                  className="net-stats-label"
                  style={{
                    color: diff <= 0 ? '#28a745' : '#dc3545',
                    background: diff <= 0 ? 'rgba(40, 167, 69, 0.1)' : 'rgba(220, 53, 69, 0.1)',
                  }}
                >
                  {labelText}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  render() {
    return (
      <Draggable position={this.state.pos} onStop={this.handleDrag} axis="both" bounds="parent">
        <div
          className={`box_white_shadow chart_averages_box long_title rates ${this.state.isFullScreen ? 'fullscreen' : ''}`}
          style={{
            width: this.state.width,
            height: this.state.height,
            left: this.state.left,
            position: this.state.isFullScreen ? 'absolute' : ''
          }}
        >
          <div className="box_white_title">
            <h3>Arrival & Throughput Rates</h3>
            { !this.state.isTrendChart && (
              <OverlayTrigger
                placement="left"
                overlay={
                  <Tooltip id="labelsTooltip" className="tooltip_dark">
                    {this.state.isLabelsVisible ? 'Hide labels' : 'Show labels'}
                  </Tooltip>
                }>
                <div className="labels-toggle" onClick={this.handleToggleLabels}>
                  <img
                    src={arrowDownIcon}
                    alt="toggle labels"
                    style={{ transform: this.state.isLabelsVisible ? 'rotate(180deg)' : 'rotate(0deg)' }}
                  />
                </div>
              </OverlayTrigger>
            )}
            <div className='right-actions'>
              {
                !this.state.isTrendChart && this.state.isFullScreen && (
                  <Button variant='secondary' size='sm' className='btn-action' onClick={this.handleToggleStats}>
                    <img src={arrowDownIcon} alt="type" style={{ transform: this.state.isStatsVisible ? 'rotate(180deg)' : 'rotate(0deg)' }} />
                  </Button>
                )
              }
              <Button variant='secondary' size='sm' className='btn-action' onClick={this.handleSwitch}>
                <img src={this.state.isTrendChart ? barIcon : lineIcon} alt="type" />
              </Button>
              <Button variant='secondary' size='sm' className={`btn-action ${this.state.isFullScreen ? 'fullscreen' : ''}`} onClick={this.handleResize}>
                <img src={this.state.isFullScreen ? expandIcon : collapseIcon} alt="toggleFullSreen" />
              </Button>
              <Button className="btn-action" onClick={this.handleClose}>&times;</Button>
            </div>
          </div>
          <div id="arrival_dep_chart_container" />
          {this.renderNetStatsPanel()}
        </div>
      </Draggable>
    );
  }
}

const mapDispatchToProps = dispatch => {
  return {
    close: () => dispatch(updateAvgChart(false, 'cfd')),
    updateWidgetMode: type => dispatch(updateWidgetMode(type, 'cfd')),
    saveConfig: () => dispatch(saveConfig())
  };
};

const mapStateToProps = state => {
  return {
    app: state.app.app,
    customFields: state.app.data.customFields,
    dateFormat: state.filters.dateFormat,
    widgetMode: state.filters.cfd.widgetMode,
    breakdownBy: state.filters.cfd.enabledColors,
    timeUnit: state.filters.cfd.timeUnit
  };
};

export default withChartExport(connect(mapStateToProps, mapDispatchToProps)(ArrivalDepChart));
